// NIGHT-DIRECTSTAFF-2026-10-16 §1: "Needs a human" -- every lead that replied and has had no human follow-up, newest
// reply first. Reads only what the CRM already loads (agent_conversations, the agent_messages refs, leads); no new storage.
//
// A conversation needs a human when the candidate has written (role candidate/user) and, since their LATEST message:
//   * no human has written (role 'human': a staff reply through /staff-reply, or "Handled off-system" through
//     /staff-note -- both are human rows), and
//   * the lead was not marked handled (leads.processed true -- the morning brief's rule -- with processed_at at or after
//     that reply; a handled mark from before the migration, with no time, counts as handled).
// The bot's own replies (role 'agent') are not a human follow-up. Out of the list: leads tagged spam or as a repeat
// (isSpamTagged -- the Leads view's working-list rule). One row per lead (its newest conversation); a conversation that
// matches no lead is still listed -- they replied -- under the conversation's own id.
import { leadForConversation } from './localTime';
import { isHandled } from './leadHandled';
import { isSpamTagged } from './spamMark';
import { CANDIDATE_ROLES } from './conversationTriage';
import { signedUpFor } from './signedUp';

const DAY = 864e5;
const t = (iso) => { const v = iso ? Date.parse(iso) : NaN; return Number.isNaN(v) ? null : v; };

// NEEDS-HUMAN-FIX-2026-10-16: each row says who they are and what they want without a tap -- the name, discipline and
// state; how they came in (site and form, Meta ad, job apply, voicemail, chat, or a text) with the date; what they asked
// for (the form's message, the job applied to, the voicemail transcript, else their last text); their last reply with
// its time. The id stays, as a small grey tag.
const s = (v) => (v === null || v === undefined ? '' : String(v).trim());
const rawOf = (lead) => (lead && lead.raw_payload && typeof lead.raw_payload === 'object' ? lead.raw_payload : {});

/** The person's name: the lead's name, else first + last from what the form sent; '' when there is none. */
export function leadName(lead) {
  if (!lead) return '';
  const r = rawOf(lead);
  return s(lead.name) || [s(r.firstName || r.first_name), s(r.lastName || r.last_name)].filter(Boolean).join(' ') || s(r.contact_name);
}

// form_type -> the words on the row
export const FORM_LABELS = {
  profile: 'profile form', therapist_lead: 'apply form', contact: 'contact form', lead: 'get-matched form',
  click_to_text: 'click to text', facility_staffing_request: 'facility staffing request', facility_request: 'facility request',
  facility_inquiry: 'facility inquiry', manual: 'added in the CRM', job_alert: 'job alerts sign-up', review_submission: 'review form',
  calculator_capture: 'pay calculator', quiz_capture: 'quiz', scholarship_application: 'scholarship application', scholarship: 'scholarship form',
  next_contract: 'next-contract form (actively looking)',   // DAY-DIRECTSTAFF-2026-10-28 §3: home's /next-contract, live on both sides since 10-26
};
/** DAY-DIRECTSTAFF-2026-10-28 §3: a lead from the next-contract form says so on Today's rows (they are looking now) */
export const activelyLooking = (lead) => !!lead && String(lead.form_type || '').trim().toLowerCase() === 'next_contract';
const VOICEMAIL_REASONS = new Set(['voicemail_received']);

/** -> { kind: 'chat'|'meta'|'job'|'voicemail'|'text'|'form', label, at } */
export function originOf(lead, conv, voicemail = null) {
  const r = rawOf(lead);
  const site = s(lead && lead.source_site);
  const ft = s(lead && lead.form_type).toLowerCase();
  const at = (lead && lead.created_at) || (conv && conv.started_at) || null;
  // NIGHT-DIRECTSTAFF-2026-10-17 §2: the widget's chat leads and job-alert sign-ups read the way the Leads list says them.
  if (ft === 'chat' || ft === 'job_alert') { const su = signedUpFor(lead); return { kind: su.kind, label: su.label, at }; }
  if ((conv && conv.channel === 'chat') || s(r.chat_session_id)) return { kind: 'chat', label: site ? `Chat on ${site}` : 'Chat', at };
  if (site === 'meta' || ft.startsWith('meta') || s(r.lead_source) === 'meta') return { kind: 'meta', label: 'Meta ad', at };
  if (ft === 'job_apply') return { kind: 'job', label: site ? `Job apply on ${site}` : 'Job apply', at };
  if (voicemail || (conv && conv.channel === 'voice')) return { kind: 'voicemail', label: 'Voicemail', at: (voicemail && voicemail.created_at) || at };
  if (!lead) return { kind: 'text', label: 'Texted in', at: (conv && conv.started_at) || null };
  const form = FORM_LABELS[ft] || (ft ? ft.replace(/_/g, ' ') : 'form');
  return { kind: 'form', label: site ? `${site} · ${form}` : form, at };
}

const MESSAGE_KEYS = ['message', 'notes', 'note', 'comments', 'comment', 'question', 'details', 'inquiry', 'how_can_we_help', 'essay'];
/** What they asked for -> { from: 'job'|'form'|'voicemail'|'text', text } ('' when nothing says). */
export function askedFor(lead, origin, voicemail, lastBody) {
  const r = rawOf(lead);
  if (origin && origin.kind === 'job') {
    const job = [s(r.job_title || r.jobTitle || r.title), s(r.job_location)].filter(Boolean).join(' · ');
    if (job) return { from: 'job', text: job };
  }
  for (const k of MESSAGE_KEYS) if (s(r[k])) return { from: 'form', text: s(r[k]) };
  if (voicemail && s(voicemail.detail)) return { from: 'voicemail', text: s(voicemail.detail) };
  if (s(lastBody)) return { from: 'text', text: s(lastBody) };
  return { from: '', text: '' };
}

/**
 * -> rows, newest reply first: { key, leadId, conversationId, shortId, name, email, source, discipline, state, origin, asked,
 *    lastReply: { body, at }, lastReplyAt, daysWaiting, phone, optOut }
 * notifications: agent_notifications (a voicemail on the conversation); lastInbound: Map(conversationId -> { body, created_at }).
 */
export function needsHumanRows({ conversations, refs, leads, notifications = [], lastInbound = new Map(), now = Date.now() }) {
  const byConv = new Map();
  for (const m of refs || []) {
    if (!m || !m.conversation_id) continue;
    const at = t(m.created_at);
    if (at === null) continue;
    const role = String(m.role || '').trim().toLowerCase();
    const e = byConv.get(m.conversation_id) || { lastIn: null, lastHuman: null };
    if (CANDIDATE_ROLES.has(role)) e.lastIn = Math.max(e.lastIn ?? -Infinity, at);
    else if (role === 'human') e.lastHuman = Math.max(e.lastHuman ?? -Infinity, at);
    byConv.set(m.conversation_id, e);
  }
  const best = new Map();                                // one row per lead (or per lead-less conversation)
  for (const c of conversations || []) {
    const e = c && byConv.get(c.id);
    if (!e || e.lastIn === null) continue;               // never replied
    if (e.lastHuman !== null && e.lastHuman >= e.lastIn) continue;   // a human followed up
    const lead = leadForConversation(c, leads);
    if (lead && isSpamTagged(lead)) continue;
    if (lead && isHandled(lead)) {
      const h = t(lead.processed_at);
      if (h === null || h >= e.lastIn) continue;         // marked handled after (or with no time) -- dealt with
    }
    const key = lead ? `lead:${lead.id}` : `conv:${c.id}`;
    const prev = best.get(key);
    if (prev && prev.lastIn >= e.lastIn) continue;
    best.set(key, { lead, conv: c, lastIn: e.lastIn });
  }
  const voicemailFor = (convId) => (notifications || []).filter((n) => n && n.conversation_id === convId && VOICEMAIL_REASONS.has(n.reason))
    .sort((a, b) => String(b.created_at).localeCompare(String(a.created_at)))[0] || null;
  return [...best.entries()].map(([key, { lead, conv, lastIn }]) => {
    const vm = voicemailFor(conv.id);
    const origin = originOf(lead, conv, vm);
    const last = lastInbound.get(conv.id) || null;
    return {
      key,
      leadId: lead ? lead.id : null,
      conversationId: conv.id,
      shortId: String(lead ? lead.id : conv.id).slice(0, 8),
      name: leadName(lead),
      email: s(lead && lead.email),
      source: (lead && lead.source_site) || '',
      discipline: (lead && lead.discipline) || '',
      state: (lead && lead.state) || '',
      origin,
      asked: askedFor(lead, origin, vm, last && last.body),
      lastReply: { body: s(last && last.body), at: new Date(lastIn).toISOString() },
      lastReplyAt: new Date(lastIn).toISOString(),
      daysWaiting: Math.max(0, Math.floor((now - lastIn) / DAY)),
      phone: (lead && lead.phone) || conv.candidate_phone || '',
      optOut: !!(lead && lead.sms_opt_out === true),
      takeover: conv.human_takeover === true,           // CRM-LEADS §3: in staff hands -> "Hand back to agent"
    };
  }).sort((a, b) => a.lastReplyAt.localeCompare(b.lastReplyAt));   // NIGHT-DIRECTSTAFF-2026-10-28 §2: oldest first (was newest)
}

/** "today", "1 day", "4 days" */
export const waitingText = (days) => (days <= 0 ? 'today' : days === 1 ? '1 day' : `${days} days`);
